<template>
  <div>
    <div v-if="inline" class="invoice-inline-preview position-relative">
      <div v-if="loading" class="invoice-inline-preview__loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 mb-0">Generating document...</p>
      </div>
      <div ref="previewContainer" v-show="!pdfUrl" class="invoice-preview-container" style="position:relative;">
        <!-- Pages will be rendered here dynamically -->
      </div>
      <iframe
        v-show="pdfUrl"
        :src="pdfUrl"
        type="application/pdf"
        width="100%"
        :height="iframeHeight"
        :title="`${documentType} ${item.reference || ''}`"
      >
        <a :href="pdfUrl" download>Click here to download the PDF.</a>
        <p>Sorry, your browser doesn't support embedded PDFs.</p>
      </iframe>
    </div>

  <CustomModal v-else-if="isModalOpen" ref="customModal" :modalId="modalId" :isOpen="isModalOpen" :showHeader="true" :showFooter="true" @modalClosed="modalClosed" :bodyStyle="'padding: 0px !important;'">
    <template #header>
      <button type="button" id="close-modal-btn" class="btn-close" aria-label="Close" @click="closeModalProgrammatically"></button>
    </template>
    <template #body>
      <div class="row h-100 m-0">
        <div :class="showSendForm ? 'col-md-8' : 'col-12'" class="p-0 position-relative">
          <div v-if="loading" style="z-index:9999;position: absolute;background: white;left: 0;right: 0;height: 0;bottom: 0;width: 100%;height: 100%;text-align: center;padding-top: 2rem;">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Generating document...</p>
          </div>
          <div ref="previewContainer" class="invoice-preview-container" v-show="isEditing || !pdfUrl" style="position:relative;">
            <!-- Pages will be rendered here dynamically -->
          </div>
          <iframe v-show="pdfUrl && !isMobile && !isEditing" :src="pdfUrl" type="application/pdf" width="100%" :height="iframeHeight">
            <a :href="pdfUrl" download>Click here to download the PDF.</a>
            <!-- Fallback content in case the browser doesn't support PDF rendering -->
            <p>Sorry, your browser doesn't support embedded PDFs.</p>
          </iframe>
          <div v-if="pdfUrl && isMobile && !isEditing" class="text-center py-5">
            <AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf" style="font-size: 64px; color: #dc3545;" />
            <h5 class="mt-3 mb-4">{{$t('document_ready')}}</h5>
            <button type="button" class="btn btn-success btn-lg" @click="downloadPdf">
              <AppIcon name="bi-download" class="bi bi-download me-2" />{{$t('download_pdf')}}
            </button>
          </div>
        </div>
        <div v-if="showSendForm" class="col-md-4 border-start p-3 bg-light">
            <div v-if="!emailSent">
                <h5 class="mb-3">{{$t('send_email')}}</h5>
                <div class="mb-3">
                    <label class="form-label">{{$t('email')}} <span class="text-danger">*</span></label>
                    <input type="email" class="form-control" v-model="emailForm.email" :placeholder="$t('enter_email_address')" style="background-color: white !important; border: 1px solid #ced4da !important;">
                </div>
                <div class="mb-3">
                    <label class="form-label">{{$t('subject')}}</label>
                    <input type="text" class="form-control" v-model="emailForm.subject" :placeholder="$t('enter_subject')" style="background-color: white !important; border: 1px solid #ced4da !important;">
                </div>
                <div class="mb-3">
                    <label class="form-label">{{$t('message')}}</label>
                    <textarea class="form-control" v-model="emailForm.message" rows="6" :placeholder="$t('enter_message')" style="background-color: white !important; border: 1px solid #ced4da !important;"></textarea>
                </div>
            </div>
            <div v-else class="h-100 d-flex flex-column justify-content-center align-items-center text-center">
                <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-success" style="font-size: 4rem;" />
                <h5 class="mt-3">{{$t('email_sent_successfully')}}</h5>
            </div>
        </div>
      </div>
    </template>
    <template #footer>
      <button v-if="showSendForm && !emailSent" type="button" class="btn btn-success" @click="sendEmail" :disabled="!emailForm.email">
        <AppIcon name="bi-send" class="bi bi-send me-1" /> {{$t('send')}}
      </button>
      
      <!-- Quick Edit Toggle -->
      <!-- <button v-if="pdfUrl && !showSendForm && !isEditing" type="button" class="btn btn-primary" @click="toggleEditMode">
        <AppIcon name="bi-pencil-square" class="bi bi-pencil-square me-1" /> {{$t('edit_invoice')}}
      </button>
      <button v-if="pdfUrl && !showSendForm && isEditing" type="button" class="btn btn-warning" @click="saveAndRegeneratePdf">
        <AppIcon name="bi-save" class="bi bi-save me-1" /> {{$t('save_and_update')}}
      </button> -->

      <button v-if="pdfUrl && !isMobile && !showSendForm" type="button" class="btn btn-success" @click="downloadPdf">
        <AppIcon name="bi-download" class="bi bi-download" />
        {{$t('download')}}</button>
      <button type="button" class="btn btn-secondary" @click="closeModalProgrammatically"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
    </template>
  </CustomModal>
  </div>
</template>

<script>
import CustomModal from '../CustomModal.vue'
import helper from "../../helper";
import html2pdf from 'html2pdf.js'
import { amountInWords } from '../../helpers/amountInWords'

// Document type translations
const documentTypeTranslations = {
  'fr': {
    'quote': 'DEVIS',
    'invoice': 'FACTURE',
    'receipt': 'REÇU',
    'purchase': 'BON DE COMMANDE',
    'order': 'BON DE COMMANDE',
    'reception': 'BON DE RECEPTION',
    'credit-invoice': "FACTURE D'AVOIR",
    'delivery': 'BON DE LIVRAISON',
    'return': "RETOUR",
    'payment': 'FACTURE'
  },
  "en": {
    "quote": "QUOTE",
    "invoice": "INVOICE",
    "receipt": "RECEIPT",
    "purchase": "PURCHASE ORDER",
    "order": "PURCHASE ORDER",
    "reception": "RECEIVING NOTE",
    "credit-invoice": "CREDIT NOTE",
    "delivery": "DEL NOTE",
    "return": "RETURN",
    "payment": "INVOICE"
  },
  "ar": {
    "quote": "عرض سعر",
    "invoice": "فاتورة",
    "receipt": "إيصال",
    "purchase": "أمر شراء",
    "order": "أمر شراء",
    "reception": "إشعار استلام",
    "credit-invoice": "إشعار ائتمان",
    "delivery": "إشعار تسليم",
    "return": "إرجاع",
    "payment": "فاتورة"
  }
}

import defaultTemplate from '../../defaultTemplate.json'

// A4 page constants (in px at 96 DPI)
const PAGE_HEIGHT = 1120;
const PAGE_PADDING_TOP = 20;
const PAGE_PADDING_BOTTOM = 20;
const HEADER_HEIGHT = 260;        // header + business info + receiver
const FOOTER_HEIGHT = 60;         // bottom page footer
const TABLE_HEADER_HEIGHT = 45;   // thead height
const TABLE_ROW_HEIGHT = 35;      // approximate height per product row
const SUMMARY_HEIGHT = 200;       // totals + notes + amount in words section
const PAGE_NUMBER_HEIGHT = 25;    // page number indicator

export default {
  name: "DocumentTemplateModal",
  components: {
    CustomModal
  },
  props: {
    type: {
      default: "invoice",
      type: String,
    },
    data: {
      type: Object,
    },
    isModalOpen: {
      type: Boolean,
      default: false
    },
    modalId: {
      type: String,
      default: 'documentModal'
    },
    showSendForm: {
      type: Boolean,
      default: false
    },
    defaultEmail: {
      type: String,
      default: ''
    },
    emailSent: {
      type: Boolean,
      default: false
    },
    businessOverride: {
      type: Object,
      default: null
    },
    customTemplate: {
      type: String,
      default: null
    },
    inline: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      documentType: "",
      client: {},
      supplier: {},
      receiver: {},
      item: {
            "reference": "Q-0021-14-11-2025",
            "date": "2025-11-14T00:00:00.000Z",
            "notes": null,
            "sub_total": 3000,
            "total_vat": 600,
            "total_discount": 0,
            "total": 3600,
            "status": "pending",
            "products": [
                {
                    "id": "af725cdc-549f-4f61-ade6-d2e16513362c",
                    "inventory_id": "27559a90-a688-4e60-a351-35f4c9af39b8",
                    "product_id": "e88dd8e4-75ae-4578-adf7-7f34d798b319",
                    "product_label": "Test Product 1",
                    "purchase_price": 2500,
                    "custom_product": false,
                    "price": 3000,
                    "discount": 0,
                    "quantity": 1,
                    "vat": 20,
                    "total": 3600,
                    "sub_total": 3000,
                }
            ],
            "client": {
                "reference": "CL-6",
                "name": "Test Client",
                "email": "testclient@example.com",
            }
      },
      templateType: this.type,
      logoBase64: null,
      stampBase64: null,
      template: null,
      pdfUrl: null,
      loading: true,
      isEditing: false,
      fileName: 'document.pdf',
      isMobile: false,
      invoiceLanguage: 'en',
      configuredDateFormat: 'YYYY-MM-DD',
      totalPages: 1,
      emailForm: {
        email: '',
        subject: '',
        message: ''
      }
    };
  },
  computed: {
    totalPrice() {
      if (this.item && Array.isArray(this.item.products) && this.item.products.length) {
        return this.item.products.reduce((sum, product) => sum + (Number(product.total) || 0), 0);
      }
      return Number(this.item?.total) || 0;
    },
    totalHtPrice() { return this.item.sub_total || 0 },
    totalDiscounts() { return this.item.total_discount || 0 },
    netHtAfterDiscount() {
      return (parseFloat(this.totalHtPrice) || 0) - (parseFloat(this.totalDiscounts) || 0);
    },
    discountPercent() {
      const subTotal = parseFloat(this.totalHtPrice) || 0;
      const discount = parseFloat(this.totalDiscounts) || 0;
      return subTotal > 0 ? (discount / subTotal) * 100 : 0;
    },
    totalTvaPrice() { return this.item.total_vat || 0 },
    business() { return this.businessOverride || this.$store.getters.BUSINESS || {}; },
    documentCurrency() { return this.item?.currency || this.business?.currency || '$'; },
    user() { return this.$store.getters.USER; },
    iframeHeight() {
      // Keep every page directly readable in the details-page preview.
      const pageHeight = this.inline ? 1120 : 750;
      const minimumHeight = this.inline ? 900 : 700;
      return Math.max(minimumHeight, this.totalPages * pageHeight) + 'px';
    }
  },
  methods: {
    formatDiscountPercent(value) {
      return String(Math.round((parseFloat(value) || 0) * 100) / 100);
    },
    truncateClientName(value, maxLength = 35) {
      const name = String(value || '').trim();
      if (name.length <= maxLength) return name;
      return `${name.slice(0, maxLength - 3).trimEnd()}...`;
    },
    toggleEditMode() {
      this.isEditing = true;
    },
    saveAndRegeneratePdf() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.isEditing = false;
      this.loading = true;
      setTimeout(() => {
        this.generatePDF();
      }, 100);
    },
    closeModalProgrammatically() {
      if (this.$refs.customModal) {
        this.$refs.customModal.closeModal();
      } else {
        this.modalClosed();
      }
    },
    //helper functions
    formatDate(date) {
      const businessDateFormat = this.business?.metadata?.date_format;
      return helper.formatDate(date, false, businessDateFormat || this.configuredDateFormat);
    },
    formatMoney(number) {
      number = number ? number : 0;
      return helper.formatMoney(number, this.documentCurrency);
    },
    letterToNumbers(amount, explicitLang = null) {
      // Use explicit language if provided, otherwise invoice language instead of user language
      const lang = explicitLang || this.invoiceLanguage || 'en';
      return amountInWords(amount, this.documentCurrency, lang);
    },

    /**
     * Calculate how many product rows fit on each page
     */
    calculateProductsPerPage() {
      const usableFirstPage = PAGE_HEIGHT - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM - HEADER_HEIGHT - FOOTER_HEIGHT - TABLE_HEADER_HEIGHT - PAGE_NUMBER_HEIGHT;
      const usableNextPage = PAGE_HEIGHT - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM - FOOTER_HEIGHT - TABLE_HEADER_HEIGHT - PAGE_NUMBER_HEIGHT;

      const productsOnFirstPage = Math.floor(usableFirstPage / TABLE_ROW_HEIGHT);
      const productsOnNextPage = Math.floor(usableNextPage / TABLE_ROW_HEIGHT);

      return { productsOnFirstPage, productsOnNextPage };
    },

    /**
     * Split products into page chunks and determine which page gets the summary
     */
    paginateProducts() {
      const products = this.item.products || [];
      if (products.length === 0) return { pages: [[]], totalPages: 1 };

      const { productsOnFirstPage, productsOnNextPage } = this.calculateProductsPerPage();

      // We need to reserve space for summary on the last page
      const summaryRows = Math.ceil(SUMMARY_HEIGHT / TABLE_ROW_HEIGHT);

      const pages = [];
      let remaining = [...products];

      // First page: fewer products because of header
      // Check if all products + summary fit on page 1
      const firstPageMaxWithSummary = Math.max(1, productsOnFirstPage - summaryRows);
      
      if (remaining.length <= firstPageMaxWithSummary) {
        // Everything fits on one page
        pages.push(remaining);
        this.totalPages = 1;
        return { pages, totalPages: 1 };
      }

      // First page chunk (no summary on first page if multi-page)
      const firstChunkSize = Math.min(remaining.length, productsOnFirstPage);
      pages.push(remaining.splice(0, firstChunkSize));

      // Subsequent pages
      while (remaining.length > 0) {
        // Check if the remaining products + summary fit on this page
        const nextPageMaxWithSummary = Math.max(1, productsOnNextPage - summaryRows);
        
        if (remaining.length <= nextPageMaxWithSummary) {
          // Last page: products + summary will fit
          pages.push(remaining.splice(0));
        } else {
          // Not the last page yet - fill it up
          const chunkSize = Math.min(remaining.length, productsOnNextPage);
          pages.push(remaining.splice(0, chunkSize));
        }
      }

      this.totalPages = pages.length;
      return { pages, totalPages: pages.length };
    },

    /**
     * Extract template parts from the formatted HTML
     */
    extractTemplateParts(formattedHtml) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formattedHtml;

      // Find the header section (everything before the table)
      const table = tempDiv.querySelector('.invoice-builder-table');
      const bottomPage = tempDiv.querySelector('.invoice-builder-bottom-page');

      // Get header HTML (everything before the table-responsive div)
      let headerHtml = '';
      let summaryHtml = '';
      let footerHtml = '';
      let tableHeadHtml = '';
      let tableRowTemplateHtml = '';
      let tableResponsiveStyle = 'margin-top: 20px; min-height: 400px;';

      const tableResponsiveDiv = tempDiv.querySelector('.table-responsive');
      if (tableResponsiveDiv) {
        tableResponsiveStyle = tableResponsiveDiv.getAttribute('style') || tableResponsiveStyle;
      }

      // Collect header elements (before table)
      const children = Array.from(tempDiv.children);
      for (const child of children) {
        if (child.querySelector('.table-responsive') || child.classList.contains('table-responsive')) {
          break;
        }
        headerHtml += child.outerHTML;
      }

      // Get table head
      if (table) {
        const thead = table.querySelector('thead');
        if (thead) tableHeadHtml = thead.outerHTML;
        const tbody = table.querySelector('tbody');
        if (tbody) {
          const firstRow = tbody.querySelector('tr');
          if (firstRow) tableRowTemplateHtml = firstRow.outerHTML;
        }
      }

      // Get summary section (everything after table, before bottom-page)
      let foundTable = false;
      for (const child of children) {
        if (child.querySelector('.table-responsive') || child.classList.contains('table-responsive')) {
          foundTable = true;
          continue;
        }
        if (foundTable) {
          if (child.querySelector('.invoice-builder-bottom-page') || child.classList.contains('invoice-builder-bottom-page')) {
            footerHtml = child.outerHTML;
          } else {
            summaryHtml += child.outerHTML;
          }
        }
      }

      // If footer wasn't found as a direct child, look deeper
      if (!footerHtml && bottomPage) {
        footerHtml = bottomPage.closest('.row')?.outerHTML || bottomPage.outerHTML;
        // Remove footer from summary if it got included
        if (summaryHtml.includes('invoice-builder-bottom-page')) {
          const summaryDiv = document.createElement('div');
          summaryDiv.innerHTML = summaryHtml;
          const footerInSummary = summaryDiv.querySelector('.invoice-builder-bottom-page');
          if (footerInSummary) {
            const footerRow = footerInSummary.closest('.row');
            if (footerRow) {
              footerRow.remove();
            } else {
              footerInSummary.remove();
            }
          }
          summaryHtml = summaryDiv.innerHTML;
        }
      }

      return { headerHtml, tableHeadHtml, tableRowTemplateHtml, summaryHtml, footerHtml, tableResponsiveStyle };
    },

    /**
     * Build a single page HTML
     */
    buildPageHtml(pageIndex, totalPages, products, parts, isFirstPage, isLastPage) {
      const { headerHtml, tableHeadHtml, summaryHtml, footerHtml, tableResponsiveStyle } = parts;

      // Build product rows
      let productRowsHtml = '';
      products.forEach((product, i) => {
        // Calculate the global index across all pages
        let globalIndex = i + 1;
        if (pageIndex > 0) {
          const { productsOnFirstPage, productsOnNextPage } = this.calculateProductsPerPage();
          globalIndex = productsOnFirstPage + 1;
          for (let p = 1; p < pageIndex; p++) {
            globalIndex += productsOnNextPage;
          }
          globalIndex += i;
        }

        const row = parts.tableRowTemplateHtml
          .replace("{product.index}", globalIndex)
          .replace("{product.label}", () => helper.sanitizeRichText(product.product_label || ''))
          .replace("{product.quantity}", product.quantity || '')
          .replace(/\{product\.unity\}/g, () => String(product.unity || ''))
          .replace(/\{product\.unit\}/g, () => String(product.unity || ''))
          .replace("{product.price}", this.formatMoney(product.price || '0'))
          .replace("{product.vat}", product.vat || '0')
          .replace("{product.sub_total}", this.formatMoney(product.sub_total || 0))
          .replace("{product.total}", this.formatMoney(product.total || 0));
        productRowsHtml += row;
      });

      // Build table with a minimum height to visually fill single-product pages gracefully
      const tableStyle = 'width: 100%; border-collapse: collapse; border: 1px solid rgb(229, 231, 235); border-radius: 8px; overflow: hidden;';
      const tableHtml = `
        <div class="table-responsive" style="${tableResponsiveStyle}">
          <table class="table invoice-builder-table" style="${tableStyle}">
            ${tableHeadHtml}
            <tbody>${productRowsHtml}</tbody>
          </table>
        </div>
      `;

      // Build page number
      const pageNumberHtml = `
        <div class="invoice-page-number" style="text-align: center; font-size: 10px; color: #6b7280; padding: 5px 0;">
          ${pageIndex + 1}/${totalPages}
        </div>
      `;

      // Build footer (sticky at bottom)
      let pageFooterHtml = footerHtml || '';
      // Wrap the footer with page number in a sticky bottom container
      const bottomSection = `
        <div class="invoice-page-bottom" style="position: absolute; bottom: 0px; left: 0; right: 0; padding-bottom: 20px;">
          ${pageNumberHtml}
          ${pageFooterHtml}
        </div>
      `;

      // Assemble page
      const watermarkHtml = this.item.deleted_at ? `
        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 150px; color: rgba(255, 0, 0, 0.15); font-weight: bold; pointer-events: none; white-space: nowrap; z-index: 9999;">
          ${this.$t('deleted').toUpperCase()}
        </div>
      ` : '';

      let pageContent = watermarkHtml;
      
      if (isFirstPage) {
        pageContent += headerHtml;
      }
      
      pageContent += tableHtml;

      if (isLastPage) {
        pageContent += summaryHtml;
      }

      pageContent += bottomSection;

      // Page wrapper with A4 dimensions
      const pageBreak = pageIndex < totalPages - 1 ? 'page-break-after: always;' : '';
      const pageHtml = `
        <div class="invoice-page" style="position: relative; width: 100%; height: ${PAGE_HEIGHT}px; padding: 40px; box-sizing: border-box; overflow: hidden; ${pageBreak} background: white; font-family: var(--document-font-family);">
          <div class="invoice-page-content" style="position: relative; height: 100%;">
            ${pageContent}
          </div>
        </div>
      `;

      return pageHtml;
    },

    /**
     * Render all pages into the preview container
     */
    renderPages() {
      if (!this.template || !this.item.products) return;

      const formattedHtml = this.formatTemplate();
      const parts = this.extractTemplateParts(formattedHtml);
      const { pages, totalPages } = this.paginateProducts();

      const container = this.$refs.previewContainer;
      if (!container) return;

      let allPagesHtml = '';

      for (let i = 0; i < pages.length; i++) {
        const isFirst = i === 0;
        const isLast = i === pages.length - 1;
        allPagesHtml += this.buildPageHtml(i, totalPages, pages[i], parts, isFirst, isLast);
      }

      container.innerHTML = allPagesHtml;

      // Update logos on all pages
      if (this.logoBase64 || this.business?.logo) {
        const logos = container.querySelectorAll('.logo');
        logos.forEach(logo => {
          logo.src = this.logoBase64 || this.business.logo || '';
        });
      }

      // Update electronic stamps on all pages
      if (this.stampBase64 || this.business?.electronic_stamp) {
        const stamps = container.querySelectorAll('.electronic_stamp, .electronic-stamp');
        stamps.forEach(stamp => {
          stamp.src = this.stampBase64 || this.business.electronic_stamp || '';
        });
      }
    },

    async generatePDF() {
      const element = this.$refs.previewContainer;
      if (!element) return;

      element.classList.add('generating-pdf');

      this.normalizeWatermarks(element);

      // Fix images with base64
      const imageTag = element.getElementsByTagName('img');
      for (let i = 0; i < imageTag.length; i++) {
          imageTag[i].src = await this.getBase64Image(imageTag[i].src);
      }

      const pdfOptions = {
        margin: 0,
        filename: this.generateFileName(),
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 3 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      };

      // Only use CSS page breaks for multi-page
      if (this.totalPages > 1) {
        pdfOptions.pagebreak = { mode: ['css'] };
      }

      html2pdf().from(element).set(pdfOptions).outputPdf('blob').then(pdfBlob => {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        this.pdfUrl = pdfUrl;
        this.loading = false;
        element.classList.remove('generating-pdf');
      });
    },
    normalizeWatermarks(element) {
      const watermarkImages = element.querySelectorAll('img[data-filigrane="true"], img[alt="Filigrane"]');

      watermarkImages.forEach(image => {
        const wrapper = image.closest('[data-filigrane="true"]:not(img)') || image.parentElement;

        if (wrapper) {
          wrapper.style.setProperty('position', 'absolute', 'important');
          wrapper.style.setProperty('left', '0', 'important');
          wrapper.style.setProperty('right', '0', 'important');
          wrapper.style.setProperty('width', '100%', 'important');
          wrapper.style.setProperty('height', 'auto', 'important');
          wrapper.style.setProperty('overflow', 'hidden', 'important');
        }

        image.style.setProperty('display', 'block', 'important');
        image.style.setProperty('width', '100%', 'important');
        image.style.setProperty('max-width', '100%', 'important');
        image.style.setProperty('height', 'auto', 'important');
        image.style.setProperty('margin-left', 'auto', 'important');
        image.style.setProperty('margin-right', 'auto', 'important');
      });
    },
    hideEmptyProductUnitColumn(root) {
      if (this.business?.metadata?.show_product_unit !== true) return;

      const hasUnitValue = (this.item?.products || []).some(product =>
        product?.unity !== null &&
        product?.unity !== undefined &&
        String(product.unity).trim() !== ''
      );
      if (hasUnitValue) return;

      root.querySelectorAll('table').forEach(table => {
        let unitColumnIndex = -1;

        for (const row of table.querySelectorAll('tbody tr')) {
          const cells = Array.from(row.children);
          const index = cells.findIndex(cell => /\{product\.(?:unity|unit)\}/.test(cell.innerHTML));
          if (index !== -1) {
            unitColumnIndex = index;
            break;
          }
        }

        if (unitColumnIndex === -1) return;

        table.querySelectorAll('tr').forEach(row => {
          const cell = row.children[unitColumnIndex];
          if (cell) cell.remove();
        });

        const column = table.querySelectorAll('colgroup col')[unitColumnIndex];
        if (column) column.remove();
      });
    },
    generateFileName() {
      const docType = this.documentType.toLowerCase().replace(/\s+/g, '-');
      const ref = this.item.reference ? this.item.reference.replace(/\//g, '-') : '';
      const timestamp = new Date().getTime();
      this.fileName = `${docType}-${ref}-${timestamp}.pdf`;
      return this.fileName;
    },
    downloadPdf() {
      if (this.pdfUrl) {
        const link = document.createElement('a');
        link.href = this.pdfUrl;
        link.download = this.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    async getBase64Image(url) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const reader = new FileReader();
        await new Promise((resolve, reject) => {
          reader.onload = resolve;
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        return reader.result;
      } catch (error) {
        alert(error)
        return null
      }
    },
    formatTemplate() {
      if (!this.template) return '';
      const currentDocumentType = this.type.toLowerCase();
      const compatibleDocumentTypes = currentDocumentType === 'receipt'
        ? ['receipt', 'process']
        : [currentDocumentType];
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.template;

      // Process elements with data-invoicly-display attribute
      const elementsWithDisplayAttr = tempDiv.querySelectorAll('[data-invoicly-display]');
      elementsWithDisplayAttr.forEach(element => {
        const displayValue = element.getAttribute('data-invoicly-display');

        //empty means always
        if(displayValue == ''){
          displayValue == 'always'
        }

        if (displayValue !== 'always') {
          // Check if current document type is in the comma-separated list
          const allowedTypes = displayValue.split(',').map(type => type.trim().toLowerCase());
          if (!compatibleDocumentTypes.some(type => allowedTypes.includes(type))) {
            // Remove element if current document type is not allowed
            element.remove();
          }
        }
        // 'always' elements remain in the document
      });

      this.hideEmptyProductUnitColumn(tempDiv);

      const discountCells = tempDiv.querySelectorAll('td');
      discountCells.forEach(td => {
        const isDiscountCell = td.innerHTML.includes('{total_discount}');
        const isNetHtCell = td.innerHTML.includes('{net_ht_after_discount}');
        if (!isDiscountCell && !isNetHtCell) return;

        const row = td.closest('tr');
        if (!row) return;

        if (this.totalDiscounts == 0) {
          row.remove();
          return;
        }

        const labelCell = Array.from(row.querySelectorAll('td, th')).find(cell => cell !== td);
        if (isDiscountCell && labelCell) {
          labelCell.appendChild(document.createTextNode(` (${this.formatDiscountPercent(this.discountPercent)}%)`));
        }
      });

      // First replace all template variables
      let formattedTemplate = tempDiv.innerHTML
        .replace(/{business.name}/g, this.business.name || '')
        .replace(/{business.logo}/g, this.business.logo || '')
        .replace(/{business.electronic_stamp}/g, this.business.electronic_stamp || '')
        .replace(/{business.currency}/g, this.documentCurrency)
        .replace(/{business.email}/g, this.business.email || '')
        .replace(/{business.address}/g, this.business.address || '')
        .replace(/{business.phone}/g, this.business.phone || '')
        .replace(/{business.website}/g, this.business.website || '')
        .replace(/{business.billing_info}/g, this.business.billing_info || '')  
        .replace(/{document.reference}/g, this.item.reference || '')
        .replace(/{document.date}/g, this.formatDate(this.item.date))
        .replace(/{{currency}}/g, this.documentCurrency)
        .replace(/\{letterToNumbers\(([^)]+)\)\}/g,(match, innerContent) => {
          // Parse inner content which might be "{total}", "{total,'fr'}", "{total},'en'", etc
          const cleaned = innerContent.replace(/[{}"'\s]/g, '');
          const parts = cleaned.split(',');
          const variable = parts[0];
          const explicitLang = parts[1] || null;

          // Support nested variables like total, sub_total, etc.
          let value = 0;
          if (variable === 'total') value = this.totalPrice;
          else if (variable === 'sub_total') value = this.totalHtPrice;
          else if (variable === 'net_ht_after_discount') value = this.netHtAfterDiscount;
          else if (variable === 'total_tax') value = this.totalTvaPrice;
          else if (variable === 'total_discount') value = this.totalDiscounts;
          else if (variable.startsWith('client.')) value = this.client[variable.split('.')[1]] || 0;
          else if (variable.startsWith('supplier.')) value = this.supplier[variable.split('.')[1]] || 0;
          else if (variable.startsWith('receiver.')) value = this.receiver[variable.split('.')[1]] || 0;
          else if (!isNaN(Number(variable))) value = Number(variable);
          return this.letterToNumbers(value, explicitLang);
        })
        .replace(/{sub_total}/g, this.formatMoney(this.totalHtPrice))
        .replace(/{net_ht_after_discount}/g, this.formatMoney(this.netHtAfterDiscount))
        .replace(/{total}/g, this.formatMoney(this.totalPrice))
        .replace(/{total_tax}/g, this.formatMoney(this.totalTvaPrice))
        .replace(/{total_discount}/g, this.formatMoney(this.totalDiscounts))
        .replace(/{notes}/g, this?.item?.notes || '')
        .replace(/{client.name}/g, this.truncateClientName(this.client?.name))
        .replace(/{client.id}/g, this.client?.id || '')
        .replace(/{client.reference}/g, this.client?.reference || '')
        .replace(/{client.address}/g, this.client?.address || '')
        .replace(/{supplier.name}/g, this.supplier?.name || '')
        .replace(/{supplier.id}/g, this.supplier?.id || '')
        .replace(/{supplier.reference}/g, this.supplier?.reference || '')
        .replace(/{supplier.address}/g, this.supplier?.address || '')
        .replace(/{receiver.name}/g, this.item?.client
          ? this.truncateClientName(this.receiver?.name)
          : (this.receiver?.name || ''))
        .replace(/{receiver.reference}/g, this.receiver?.reference || '')
        .replace(/{receiver.address}/g, this.receiver?.address || '')
        .replace(/{receiver.city}/g, this.receiver?.city || '')
        .replace(/{receiver.vat_number}/g, this.receiver?.vat_number || '')
        .replace(/{document.type}/g, this.documentType)
        .replace(/{payment_term}/g, this.item?.payment_term || '')
        .replace(/{client_po_number}/g, this.item?.client_po_number || '')
        .replace(/{client_dn_number}/g, this.item?.client_dn_number || '')
        .replace(/{client_in_number}/g, this.item?.client_in_number || '')
        //translate keys
        .replace(/#product_label#/g, this.$t('product_label') || '')
        .replace(/#product_quantity#/g, this.$t('product_quantity') || '')
        .replace(/#product_unit_price#/g, this.$t('product_unit_price') || '')
        .replace(/#product_vat#/g, this.$t('product_vat') || '')
        .replace(/#product_total_ht#/g, this.$t('product_total_ht') || '')
        .replace(/#product_total#/g, this.$t('total') || '')
        .replace(/#auto_entrepreneur#/g, this.$t('auto_entrepreneur') || '')
        .replace(/#vat_not_applicable_auto_entrepreneur#/g, this.$t('vat_not_applicable_auto_entrepreneur') || '')
        .replace(/#subtotal#/g, this.$t('subtotal') || '')
        .replace(/#vat#/g, this.$t('vat') || '')
        .replace(/#discount#/g, this.$t('discount') || '')
        .replace(/#total#/g, this.$t('total') || '')
        .replace(/#amount_in_words#/g, this.$t('amount_in_words') || '')
        .replace(/#date#/g, this.$t('date') || '')
        .replace(/#u_p#/g, this.$t('u_p') || '') 
        .replace(/#sub_total#/g, this.$t('sub_total') || '')
        .replace(/#net_ht_after_discount#/g, this.$t('net_ht_after_discount') || '')
        .replace(/#product_index#/g, this.$t('product_index') || '')
        .replace(/#client_signature#/g, this.$t('client_signature') || '')
        .replace(/#supplier_signature#/g, this.$t('supplier_signature') || '')
        .replace(/#electronic_stamp#/g, this.$t('electronic_stamp') || '');


      // Merge extra_fields into business for easier access
      if (this.business && this.business.extra_fields) {
        this.business.extra_fields.forEach(field => {
          formattedTemplate = formattedTemplate.replace(new RegExp(`{business.${field.field_name}}`, 'g'), field.field_value || '');
        });
      }

      // Replace any remaining {business.X} placeholders with an empty string
      formattedTemplate = formattedTemplate.replace(/{business\.[^}]*}/g, '');
      return formattedTemplate;
    },
    async getTemplate() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/templates-by-type?type=standard&document_type=${this.templateType}`);
        if (response.data && response.data.data) {
          this.template = helper.jsonToHtml(JSON.parse(response.data.data));
          let metadata = response.data.metadata;
          if (typeof metadata === 'string') {
            try {
              metadata = JSON.parse(metadata);
            } catch (error) {
              metadata = {};
            }
          }
          const supportedFormats = [
            'YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY/DD/MM',
            'DD-MM-YYYY', 'DD/MM/YYYY', 'DD/YYYY/MM',
            'MM-DD-YYYY', 'MM/DD/YYYY', 'MM/YYYY/DD'
          ];
          if (metadata && supportedFormats.includes(metadata.date_format)) {
            this.configuredDateFormat = metadata.date_format;
          }
        }else{
          this.template = helper.jsonToHtml(defaultTemplate);
        }
      } catch (error) {
        alert(error)
        return null
        // Fallback to default template if available
        // this.useDefaultTemplate();
      }
    },
    modalClosed() {
      // Restore app direction based on user language
      const appLanguage = this.user?.language || localStorage.getItem('currentLang') || 'en';
      const isRTL = appLanguage === 'ar';
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      
      this.isModalOpen = false;
      this.$emit('modalClosed', true);
    },
    async sendEmail() {
        if (!this.pdfUrl) return;
        
        try {
            this.loading = true;
            const response = await fetch(this.pdfUrl);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
                const base64data = reader.result;
                this.$emit('sendEmail', {
                    ...this.emailForm,
                    pdf_base64: base64data,
                    filename: this.fileName
                });
                this.loading = false;
            }
        } catch (error) {
            this.loading = false;
            alert('Error generating PDF for email');
        }
    }
  },
  async mounted() {
    this.loading = true;
    // Detect mobile (Bootstrap sm breakpoint + user agent check)
    this.isMobile = window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // Get document data
    this.item = this.data || this.item;
    this.client = this.item.client || {};
    this.supplier = this.item.supplier || {};
    
    if(this.defaultEmail){
        this.emailForm.email = this.defaultEmail
    }

    // Set default subject
    if (this.item.reference) {
        this.emailForm.subject = this.item.reference;
    }
    
    // Get invoice language from business metadata or fallback to business language
    const invoiceLanguage = this.business?.metadata?.invoice_language || this.business?.language || this.user.language || 'en';
    this.invoiceLanguage = invoiceLanguage;
    
    // Setup document type based on type and invoice language
    const typeLower = this.type.toLowerCase();
    this.documentType = typeLower === 'receipt'
      ? (this.business?.metadata?.process_document_name || documentTypeTranslations[invoiceLanguage]?.receipt || documentTypeTranslations.en.receipt)
      : (documentTypeTranslations[invoiceLanguage]?.[typeLower] || documentTypeTranslations['en'][typeLower] || this.type.toUpperCase());

    // Set document direction based on invoice language (not app language)
    const isRTL = invoiceLanguage === 'ar';
    if (!this.inline) {
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }
    
    // Also set on the invoice container for better compatibility
    const invoiceContainer = this.$refs.previewContainer;
    if (invoiceContainer) {
      invoiceContainer.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }

    // Determine and prepare receiver
    this.receiver = this.item.client || this.item.supplier || {};

    // Get template
    if (this.customTemplate) {
      this.template = this.customTemplate;
    } else {
      await this.getTemplate();
    }

    this.logoBase64 = this.business?.logo ? await this.getBase64Image(this.business.logo) : null;
    this.stampBase64 = this.business?.electronic_stamp ? await this.getBase64Image(this.business.electronic_stamp) : null;

    // Render paginated pages
    if (this.template) {
      this.renderPages();
    }

    // Generate PDF
    setTimeout(() => {
      this.generatePDF();
    }, 500);
  },
  beforeDestroy() {
    if (this.pdfUrl) {
      URL.revokeObjectURL(this.pdfUrl);
    }
  }
};
</script>
<style>
.invoice-message-mobile {
  display: none;
}

/* Page styles */
.invoice-page {
  font-family: var(--document-font-family);
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 10px;
}
.generating-pdf .invoice-page {
  box-shadow: none !important;
  margin-bottom: 0px !important;
}

.invoice-page + .invoice-page {
  border-top: 2px dashed #ccc;
}
.generating-pdf .invoice-page + .invoice-page {
  border-top: none !important;
}

/* Footer adjustments */
.invoice-builder-bottom-page {
    position: relative !important;
    padding-top:5px;
    left : 0 !important;
    right : 0 !important;
    width: 100% !important;
    padding: 5px 0;
}

.invoice-page-bottom {
  position: absolute !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
}

.custom-modal-hidden {
  position: absolute;
  left: -9999px;
  top: -9999px;
}

.table-responsive , .invoice-builder-table{
  background-color: white !important;
}

.invoice-message-mobile {
  display: none;
}
@media only screen and (max-width: 600px) {
  body {
    width: auto !important;
  }
  table[class="fullTable"] {
    width: 96% !important;
    clear: both;
  }
  table[class="fullPadding"] {
    width: 85% !important;
    clear: both;
  }
  table[class="col"] {
    width: 45% !important;
  }
  .erase {
    display: none;
  }

  .invoice-preview-container {
    padding: 2rem;
  }

  .invoice {
    display: none;
  }

  .invoice-message-mobile {
    display: block;
  }
}

@media only screen and (max-width: 420px) {
  table[class="fullTable"] {
    width: 100% !important;
    clear: both;
  }
  table[class="fullPadding"] {
    width: 85% !important;
    clear: both;
  }
  table[class="col"] {
    width: 100% !important;
    clear: both;
  }
  table[class="col"] td {
    text-align: left !important;
  }
  .erase {
    display: none;
    font-size: 0;
    max-height: 0;
    line-height: 0;
    padding: 0;
  }
  .visibleMobile {
    display: block !important;
  }
  .hiddenMobile {
    display: none !important;
  }
}

.invoice-table td {
  padding: 0 5px;
}

.invoice p {
  margin-bottom: 0;
}

.invoice .document-type {
  margin-bottom: 2rem;
}

.invoice .logo {
  width: 3cm;
}

.invoice .invoice-table {
  margin-top: 1rem;
}

.invoice .table-container {
  min-height: 300px;
}

.invoice .table-total tr td {
  line-height: 1rem !important;
  padding: 5px 0;
}

#logo {
  width: 100px;
  height: 100px;
}

.invoice-preview-container {
  padding: 0px !important;
}
.invoice-inline-preview {
  min-height: 900px;
  overflow: hidden;
  background: #f3f4f6;
}
.invoice-inline-preview iframe {
  display: block;
  border: 0;
  background: white;
}
.invoice-inline-preview__loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  min-height: 900px;
  padding-top: 3rem;
  text-align: center;
  background: white;
}
.editing-mode {
  border: 2px dashed #0d6efd !important;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
}
.editing-mode:focus-visible {
  outline: none;
  background-color: rgba(13, 110, 253, 0.02);
}
.table>:not(caption)>*>*{
  padding: 0 !important;
}

/* Page number styling */
.invoice-page-number {
  font-family: inherit;
  color: #9ca3af;
  letter-spacing: 0.5px;
}
</style>
